/**
 * Pickup time picker - ASAP option + day/time grid based on restaurant hours
 */

import { useMemo, useState, useEffect } from 'react'
import { Zap } from 'lucide-react'
import { Button, DividerWithLabel } from '@/components/ui'
import { restaurantConfig } from '@/config/restaurant'

interface PickupTimePickerProps {
  value: string
  onChange: (time: string) => void
  disabled?: boolean
}

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const
type DayKey = (typeof DAY_NAMES)[number]

/** Parse "11:00 AM - 10:00 PM" → { open, close } in minutes from midnight */
function parseHoursString(hoursStr: string): { open: number; close: number } | null {
  const match = hoursStr.match(/(\d+):(\d+)\s*(AM|PM)\s*[-–]\s*(\d+):(\d+)\s*(AM|PM)/i)
  if (!match) return null

  let openH = parseInt(match[1]),
    closeH = parseInt(match[4])
  const openM = parseInt(match[2]),
    closeM = parseInt(match[5])

  if (match[3].toUpperCase() === 'PM' && openH !== 12) openH += 12
  if (match[3].toUpperCase() === 'AM' && openH === 12) openH = 0
  if (match[6].toUpperCase() === 'PM' && closeH !== 12) closeH += 12
  if (match[6].toUpperCase() === 'AM' && closeH === 12) closeH = 0

  return { open: openH * 60 + openM, close: closeH * 60 + closeM }
}

function minutesToLabel(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`
}

/** Generate 30-min pickup slots for the given date, filtering past times if today */
function generateSlots(date: Date, isToday: boolean): string[] {
  const dayKey: DayKey = DAY_NAMES[date.getDay()]
  const parsed = parseHoursString(restaurantConfig.hours[dayKey])
  if (!parsed) return []

  let startAt = parsed.open
  if (isToday) {
    const now = new Date()
    const currentMinutes = now.getHours() * 60 + now.getMinutes()
    // Earliest slot must be at least 30 min in the future, rounded up to next half-hour
    startAt = Math.max(startAt, Math.ceil((currentMinutes + 30) / 30) * 30)
  }

  const slots: string[] = []
  for (let m = startAt; m < parsed.close; m += 30) {
    slots.push(minutesToLabel(m))
  }
  return slots
}

export function PickupTimePicker({ value, onChange, disabled = false }: PickupTimePickerProps) {
  const [selectedDay, setSelectedDay] = useState<'today' | 'tomorrow'>(
    value.startsWith('Tomorrow') ? 'tomorrow' : 'today'
  )

  // Auto-select ASAP on first render if no value yet
  useEffect(() => {
    if (!value) onChange('ASAP')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync day tab when value changes externally (e.g. reset)
  useEffect(() => {
    if (value.startsWith('Tomorrow')) setSelectedDay('tomorrow')
    else if (value.startsWith('Today')) setSelectedDay('today')
  }, [value])

  const slots = useMemo(() => {
    const date = new Date()
    if (selectedDay === 'tomorrow') date.setDate(date.getDate() + 1)
    return generateSlots(date, selectedDay === 'today')
  }, [selectedDay])

  const isASAP = value === 'ASAP'
  // Extract time portion: "Today, 2:30 PM" → "2:30 PM"
  const selectedSlot = value.replace(/^(Today|Tomorrow), /, '')
  const isSlotFromCurrentTab =
    (selectedDay === 'today' && value.startsWith('Today')) ||
    (selectedDay === 'tomorrow' && value.startsWith('Tomorrow'))

  return (
    <div className="space-y-5">
      {/* ASAP card */}
      <Button
        variant="outline"
        className={`w-full flex items-center gap-3 p-4 h-auto rounded-xl text-left justify-start ${
          isASAP ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
        }`}
        onClick={() => onChange('ASAP')}
        disabled={disabled}
      >
        <div className={`p-2 rounded-full ${isASAP ? 'bg-primary/15' : 'bg-muted'}`}>
          <Zap className={`h-4 w-4 ${isASAP ? 'text-primary' : 'text-muted-foreground'}`} />
        </div>
        <div>
          <p className={`font-semibold text-sm ${isASAP ? 'text-primary' : ''}`}>
            As Soon As Possible
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 font-normal">
            We&apos;ll prepare your order right away
          </p>
        </div>
      </Button>

      {/* Divider with label */}
      <DividerWithLabel label="or choose a time" />

      {/* Day tabs */}
      <div className="flex gap-2">
        {(['today', 'tomorrow'] as const).map((day) => (
          <Button
            key={day}
            variant={selectedDay === day ? 'default' : 'secondary'}
            className={`rounded-full px-5 h-9 sm:h-8 text-sm ${
              selectedDay === day
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            onClick={() => setSelectedDay(day)}
            disabled={disabled}
          >
            {day === 'today' ? 'Today' : 'Tomorrow'}
          </Button>
        ))}
      </div>

      {/* Time slot grid */}
      {slots.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {slots.map((slot) => {
            const isSelected = !isASAP && isSlotFromCurrentTab && selectedSlot === slot
            return (
              <Button
                key={slot}
                variant={isSelected ? 'default' : 'outline'}
                className={`h-11 sm:h-9 rounded-lg text-sm ${
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:border-primary/50 hover:bg-primary/5'
                }`}
                onClick={() => {
                  const dayLabel = selectedDay === 'today' ? 'Today' : 'Tomorrow'
                  onChange(`${dayLabel}, ${slot}`)
                }}
                disabled={disabled}
              >
                {slot}
              </Button>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No available pickup times for this day</p>
      )}
    </div>
  )
}
